/**
 * @voltxampere/web — `<vxa-waveform>` (Sprint 15)
 *
 * Real oscilloscope panel that replaces the Sprint 11 placeholder.
 * Pulls from four module signal stores:
 *
 *   - `channels`       — six slot states (CH1-CH4 + M1-M2)
 *   - `currentTransientResult` / `transientRunning` / `transientError`
 *                      — engine result + lifecycle (Sprint 14.0.1
 *                      runner pattern carries over)
 *   - `transientLive`  — Live-mode flag for the LIVE indicator
 *
 * Per Sprint 15 D5 carry-over (Sprint 14): all button click
 * handlers attach to the panel root via the event delegation
 * pattern (`data-action` + parent `@click`). Inline `@click=${...}`
 * is avoided because the Lit static-template parser misaligns
 * attribute interpolations under happy-dom + dev-mode.
 *
 * Per Sprint 15 D2 carry-over (Sprint 11/12): channel control rows
 * + measurement tiles render via array-return TemplateResult
 * fragments — Lit's interpolation matcher is more reliable than
 * static template + array.map at depth.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { signal } from "@preact/signals-core";
import {
  WaveformDirtyTracker,
  WaveformRenderer,
  WaveformViewport,
  CHANNEL_IDS,
  PROBE_CHANNEL_IDS,
  MATH_CHANNEL_IDS,
  MEASUREMENTS,
  detectCycle,
  evaluateMath,
  parseMathExpression,
  type ChannelId,
  type ChannelView,
  type MeasurementId,
  type TriggerOverlay,
} from "@voltxampere/ui/waveform";
import { ZENIT_THEME } from "@voltxampere/ui/canvas";
import type { Theme } from "@voltxampere/ui";

import {
  channels,
  setVDiv,
  toggleChannel,
  unbindProbe,
  setMathExpression,
} from "../store/channels.js";
import {
  currentTransientResult,
  runTransient,
  startLive,
  stopLive,
  transientError,
  transientLive,
  transientRunning,
} from "../store/transient-runner.js";
import { currentCircuit } from "../store/circuit.js";
import {
  cursorDeltaT,
  cursorInverseDeltaT,
  nudgeCursor,
  setCursor,
  snapTimeToSample,
  toggleCursors,
  waveformCursors,
  type CursorId,
} from "../store/waveform-cursor.js";
import { SignalController } from "../lit-utils/signal-controller.js";
import { t, type TranslationKey } from "../i18n/index.js";

/**
 * Sprint 18 Paket C C2 — six MEASUREMENTS-driven tiles + two
 * cursor-derived virtual tiles (Δt + 1/Δt) bring the panel from
 * 5/8 to 8/8 mockup parity. Order mirrors zenit-platform.html
 * lines 2308-2344 exactly: V_rms, V_peak, V_pp, Frekans, Periyot,
 * Duty, Δt, 1/Δt.
 */
const DEFAULT_ACTIVE_MEASUREMENTS: readonly MeasurementId[] = [
  "vrms",
  "vmax",
  "vpp",
  "frequency",
  "period",
  "duty-cycle",
];

/** Custom virtual tile ids for cursor-derived readings — they
 *  are not part of the MEASUREMENTS map (which is pure-function
 *  over a sample buffer); they pull from the cursor store. */
type CursorTileId = "cursor-dt" | "cursor-invdt";

interface MeasurementTile {
  readonly id: MeasurementId | CursorTileId;
  readonly labelKey: TranslationKey;
}

const TRIGGER_DEFAULT: TriggerOverlay | null = null;

/** Visibility flag — F4 toggles. Module-scoped so the shortcut
 *  catalog can flip it without keeping a reference to the
 *  component instance. */
export const waveformExpanded = signal<boolean>(true);

@customElement("vxa-waveform")
export class VxaWaveform extends LitElement {
  private readonly _channels = new SignalController(this, channels);
  private readonly _result = new SignalController(this, currentTransientResult);
  private readonly _running = new SignalController(this, transientRunning);
  private readonly _live = new SignalController(this, transientLive);
  private readonly _error = new SignalController(this, transientError);
  private readonly _expanded = new SignalController(this, waveformExpanded);
  private readonly _cursors = new SignalController(this, waveformCursors);
  private readonly _cursorDt = new SignalController(this, cursorDeltaT);
  private readonly _cursorInvDt = new SignalController(this, cursorInverseDeltaT);

  /** Live drag target — populated by pointerdown on a cursor handle,
   *  consumed by pointermove + pointerup. We keep this as a plain
   *  field (not a signal) because it never affects render state. */
  private dragCursor: CursorId | null = null;

  @state() private activeMeasurements: readonly MeasurementId[] = DEFAULT_ACTIVE_MEASUREMENTS;
  /** Estimated sample rate from the most recent transient result.
   *  Defaults to 1 MHz when no result has landed yet — gives
   *  measurements a non-zero divisor to avoid surfacing all-null
   *  results on a freshly-loaded panel. */
  @state() private sampleRate = 1_000_000;

  // The canvas is only rendered while the panel is expanded.
  // `@query` produces a getter that returns `T | null`; we treat
  // the missing case explicitly in `firstUpdated` + `applyResize`.
  @query("canvas") declare private readonly canvasEl: HTMLCanvasElement | null;

  private renderer: WaveformRenderer | null = null;
  private viewport = new WaveformViewport();
  private dirty = new WaveformDirtyTracker();
  private themeSignal = signal<Theme>(ZENIT_THEME);
  private triggerSignal = signal<TriggerOverlay | null>(TRIGGER_DEFAULT);
  private channelViewSignal = signal<ReadonlyMap<ChannelId, ChannelView>>(new Map());

  static override styles = css`
    :host {
      display: block;
      height: 100%;
      min-height: 0;
      background: var(--void);
      color: var(--ivory);
      border-top: 1px solid var(--brass-faint);
      /* v1.0.2 hotfix ADR-0106 KOL-B-FONT-2 — was var(--font-sans, "Inter") */
      font-family: var(--font-mono);
    }
    .waveform {
      display: grid;
      grid-template-rows: 32px 1fr 80px;
      grid-template-columns: 1fr 240px;
      grid-template-areas:
        "topbar topbar"
        "canvas measurements"
        "channels measurements";
      height: 100%;
      gap: 1px;
      background: var(--ivory-wisp);
    }
    .topbar,
    .canvas-pane,
    .measurements,
    .channels {
      background: var(--ink);
    }
    .topbar {
      grid-area: topbar;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 0 12px;
      font-size: 12px;
    }
    .topbar .live {
      color: var(--danger);
      font-weight: 600;
      /* Sprint 18 D11 — pulse the live status pill so the user
       * gets a "system is breathing" cue while transient streams. */
      animation: livePulse 1.6s ease-in-out infinite;
    }
    .topbar .ready {
      color: var(--ivory-dim);
    }
    @keyframes livePulse {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.55;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .topbar .live {
        animation: none;
      }
    }
    .topbar .timebase {
      color: var(--brass);
      font-variant-numeric: tabular-nums;
    }
    .topbar button {
      background: var(--ink2);
      border: 1px solid var(--brass-faint);
      color: var(--ivory);
      font-family: inherit;
      font-size: 11px;
      padding: 4px 10px;
      cursor: pointer;
      border-radius: 2px;
    }
    .topbar button:hover {
      background: var(--ink3);
    }
    .canvas-pane {
      grid-area: canvas;
      position: relative;
      overflow: hidden;
    }
    canvas {
      display: block;
      width: 100%;
      height: 100%;
    }
    /* Sprint 18 Paket C C1 — Δt cursor pair */
    .cursor-overlay {
      position: absolute;
      inset: 0;
      pointer-events: none;
    }
    .cursor-line {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 1px;
      background: linear-gradient(
        to bottom,
        var(--brass) 0,
        var(--brass) 4px,
        transparent 4px,
        transparent 8px
      );
      background-size: 1px 8px;
      background-repeat: repeat-y;
      opacity: 0.8;
      transform: translateX(-0.5px);
      pointer-events: none;
    }
    .cursor-handle {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 10px;
      transform: translateX(-5px);
      cursor: ew-resize;
      pointer-events: auto;
      z-index: 2;
    }
    .cursor-label {
      position: absolute;
      top: 4px;
      transform: translateX(-50%);
      font-family: var(--font-mono, "JetBrains Mono", monospace);
      font-size: 10px;
      color: var(--brass);
      pointer-events: none;
      text-align: center;
      white-space: nowrap;
    }
    .topbar .cursor-readout {
      font-family: var(--font-mono, "JetBrains Mono", monospace);
      font-size: 11px;
      color: var(--brass-faint);
      letter-spacing: 0.02em;
    }
    .topbar .cursor-readout em {
      color: var(--brass);
      font-style: normal;
    }
    .topbar button[data-action="toggle-cursors"][aria-pressed="true"] {
      color: var(--brass);
      border-color: var(--brass);
    }
    .measurements {
      grid-area: measurements;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1px;
      padding: 0;
      background: var(--ivory-wisp);
    }
    .measurement-tile {
      background: var(--ink);
      padding: 6px 8px;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .measurement-tile .label {
      font-size: 10px;
      font-style: italic;
      color: var(--brass);
    }
    .measurement-tile .value {
      font-family: var(--font-mono, "JetBrains Mono", monospace);
      font-size: 13px;
      color: var(--ivory);
    }
    .measurement-tile .unit {
      font-size: 10px;
      font-style: italic;
      color: var(--brass-faint);
    }
    .channels {
      grid-area: channels;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 0 12px;
      overflow-x: auto;
      font-size: 11px;
    }
    .channel-chip {
      display: flex;
      align-items: center;
      gap: 6px;
      background: var(--ink2);
      border: 1px solid var(--brass-faint);
      padding: 3px 8px;
      cursor: pointer;
    }
    .channel-chip[data-enabled="true"] {
      background: var(--ink3);
    }
    .channel-chip .swatch {
      width: 10px;
      height: 10px;
      border-radius: 2px;
    }
    .math-input {
      flex: 1;
      min-width: 120px;
      background: var(--void);
      border: 1px solid var(--brass-faint);
      color: var(--ivory);
      font-family: var(--font-mono, "JetBrains Mono", monospace);
      font-size: 11px;
      padding: 2px 4px;
    }
    .math-error {
      color: var(--danger);
      font-size: 10px;
    }
    .collapsed {
      height: 24px;
      width: 100%;
      display: flex;
      align-items: center;
      padding: 0 12px;
      font-size: 11px;
      color: var(--brass);
      border-top: 1px solid var(--brass-faint);
      background: var(--ink);
      /* Sprint 80.Z A2 — the collapsed strip is now a <button>,
         strip the UA chrome (border, native font, default cursor)
         so it visually matches the previous <div>. */
      border-left: 0;
      border-right: 0;
      border-bottom: 0;
      font-family: var(--font-mono);
      letter-spacing: 0.05em;
      cursor: pointer;
      text-align: left;
      transition: color var(--transition-hover);
    }
    .collapsed:hover {
      color: var(--brass-hot);
    }
    .collapsed:focus-visible {
      outline: 1px solid var(--brass);
      outline-offset: -2px;
    }
    /* Sprint 19 F3 — empty-state CTA when no channel is probe-bound. */
    .empty-state {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      pointer-events: none;
      background: rgba(7, 11, 24, 0.6);
      backdrop-filter: blur(2px);
    }
    .empty-state .title {
      font-family: var(--font-serif, "EB Garamond", Georgia, serif);
      font-style: italic;
      font-size: 16px;
      color: var(--brass);
    }
    .empty-state .hint {
      font-family: var(--font-mono, "JetBrains Mono", monospace);
      font-size: 11px;
      color: var(--ivory-dim, #aab1c5);
    }
  `;

  override render(): TemplateResult {
    if (!this._expanded.value) {
      // Sprint 80.Z A2 (Şefim #3 closure) — collapsed strip must be
      // a real button so clicking it expands the pane. Previously
      // the strip rendered as a non-interactive <div> with a "▴
      // Waveform (F4 to expand)" hint; Şefim's tour observation
      // "gizleme butonu tersine çalışıyor" actually was "I clicked
      // the ▴ symbol and nothing happened — the toggle is one-way".
      // The L-A-2 topbar chip + Cmd+Shift+O are the canonical full-
      // hide path; this collapsed-strip control is the canonical
      // 24 px ↔ 260 px in-pane expand path.
      return html`<button
        class="collapsed"
        type="button"
        @click=${this.handleAction.bind(this)}
        data-action="collapse"
        aria-label="Osiloskop panelini genişlet"
        title="Genişlet (▴ veya F4)"
      >
        ▴ Waveform (F4 to expand)
      </button>`;
    }
    void this._channels.value;
    void this._result.value;
    void this._running.value;
    void this._live.value;
    void this._error.value;
    void this._cursors.value;
    void this._cursorDt.value;
    void this._cursorInvDt.value;
    return html`<div
      class="waveform"
      @click=${this.handleAction.bind(this)}
      @input=${this.handleInput.bind(this)}
    >
      ${this.renderTopBar()}
      <div class="canvas-pane">
        <canvas></canvas>
        ${this.renderCursorOverlay()} ${this.renderEmptyState()}
      </div>
      ${this.renderMeasurementPanel()} ${this.renderChannelControls()}
    </div>`;
  }

  /** True when at least one probe channel (CH1-CH4) has a net
   *  attached. Math channels are intentionally ignored — they
   *  derive from probe data, so a math-only configuration with
   *  no probes is functionally identical to "no signal". */
  private hasAnyProbeBound(): boolean {
    const map = this._channels.value;
    for (const id of PROBE_CHANNEL_IDS) {
      const state = map.get(id);
      if (state?.probeBinding) return true;
    }
    return false;
  }

  /** Sprint 19 F3 — empty-state CTA. The renderer keeps drawing
   *  the grid behind us, but we overlay a translucent panel that
   *  tells a first-time user where the data goes. The DOM node
   *  is queryable via `[data-testid="waveform-empty"]` so the
   *  happy-dom test suite can assert presence/absence without
   *  poking at the canvas pixels. */
  private renderEmptyState(): TemplateResult | typeof nothing {
    if (this.hasAnyProbeBound()) return nothing;
    return html`<div class="empty-state" data-testid="waveform-empty">
      <span class="title">${t("waveform.empty.title")}</span>
      <span class="hint">${t("waveform.empty.hint")}</span>
    </div>`;
  }

  override firstUpdated(): void {
    if (!this.canvasEl) return;
    this.refreshChannelViewSignal();
    const ctx = this.canvasEl.getContext("2d");
    if (!ctx) return;
    this.renderer = new WaveformRenderer({
      ctx,
      canvas: this.canvasEl,
      viewport: this.viewport,
      dirty: this.dirty,
      channelsSignal: this.channelViewSignal,
      themeSignal: this.themeSignal,
      triggerSignal: this.triggerSignal,
      // Sprint 18 D3 — pass the transient live flag through so
      // `drawTrace` can paint the brass-hot leading-edge marker.
      // The renderer no-ops the marker when this signal is false
      // or when the user has reduce-motion preference set
      // (handled inside the renderer via livePhase fallback).
      liveSignal: transientLive,
    });
    this.renderer.start();
    this.applyResize();
    // Sprint 25 R3 lane 5 — resize listener registration moved to
    // `connectedCallback` so re-mounts (pane swap, expand/collapse)
    // re-bind cleanly. Keeping it here would mean a re-mounted
    // canvas whose `firstUpdated` we manually call from `updated()`
    // would double-bind on top of the already-removed disconnect.
  }

  override updated(): void {
    if (this._expanded.value && this.canvasEl && !this.renderer) {
      // Component re-mounted after a collapse; rebuild the renderer.
      this.firstUpdated();
    }
    this.refreshChannelViewSignal();
    this.refreshSampleRate();
  }

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("keydown", this.handleKeyDown);
    // Sprint 25 R3 lane 5 — TS-109 fix. Pre-Sprint-25 the resize
    // listener lived in `firstUpdated` only, so re-mounting the
    // component after a pane swap left it orphaned: the
    // `disconnectedCallback` removed the listener, the next
    // `connectedCallback` did not re-add it, and `firstUpdated` is
    // a one-shot Lit lifecycle hook by spec (we manually re-call
    // it from `updated()` for renderer rebuild, but a future
    // refactor that drops that re-call would silently break
    // resize). Binding here makes the lifecycle symmetric.
    window.addEventListener("resize", this.applyResize);
  }

  override disconnectedCallback(): void {
    window.removeEventListener("resize", this.applyResize);
    window.removeEventListener("keydown", this.handleKeyDown);
    this.renderer?.dispose();
    this.renderer = null;
    super.disconnectedCallback();
  }

  // ── cursor drag + keyboard ──────────────────────────────────────

  /** CSS-pixel x → percent of canvas width. The viewport's
   *  `timeToScreen` projects time-axis seconds onto an arbitrary
   *  pixel width; passing 100 turns the result into a percent
   *  directly, no extra math required. */
  private timeToPercent(t: number): number {
    return this.viewport.timeToScreen(t, 100);
  }

  /** Map a clientX to a sample-snapped time inside the canvas
   *  pane. Callers pass the canvas pane's bounding rect so the
   *  helper stays pure (no DOM lookup mid-drag). */
  private clientToSnappedTime(clientX: number, paneRect: DOMRect): number {
    const x = clientX - paneRect.left;
    const t = this.viewport.screenToTime(x, paneRect.width);
    const samples = this.findPrimaryChannel()?.buffer.snapshot();
    if (samples && samples.times.length > 0) {
      return snapTimeToSample(t, samples.times);
    }
    return t;
  }

  private readonly handleCursorPointerDown = (e: PointerEvent): void => {
    const target = e.currentTarget as HTMLElement | null;
    if (!target) return;
    const which = target.dataset.cursorHandle as CursorId | undefined;
    if (which !== "t1" && which !== "t2") return;
    e.preventDefault();
    target.setPointerCapture(e.pointerId);
    this.dragCursor = which;
  };

  private readonly handleCursorPointerMove = (e: PointerEvent): void => {
    if (this.dragCursor === null) return;
    const pane = (e.currentTarget as HTMLElement | null)?.parentElement;
    if (!pane) return;
    const rect = pane.getBoundingClientRect();
    if (rect.width <= 0) return;
    const t = this.clientToSnappedTime(e.clientX, rect);
    setCursor(this.dragCursor, t);
  };

  private readonly handleCursorPointerUp = (e: PointerEvent): void => {
    if (this.dragCursor === null) return;
    const target = e.currentTarget as HTMLElement | null;
    if (target?.hasPointerCapture(e.pointerId)) {
      target.releasePointerCapture(e.pointerId);
    }
    this.dragCursor = null;
  };

  /** Keyboard nudge: ←/→ moves T1, Shift+←/→ moves T2. The step is
   *  one sample width when there is a result; otherwise a fixed
   *  1% of the visible window so a freshly-loaded panel still
   *  responds. The handler is deliberately scoped to the expanded
   *  + cursor-enabled state so it never fights the schematic
   *  catalog. */
  private readonly handleKeyDown = (e: KeyboardEvent): void => {
    if (!this._expanded.value) return;
    if (!this._cursors.value.enabled) return;
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    // Skip when the user is typing into a field (math-input,
    // inspector param, search box). The catalog observes the same
    // discipline so the precedent is consistent.
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement ||
      (e.target instanceof HTMLElement && e.target.isContentEditable)
    ) {
      return;
    }
    const which: CursorId = e.shiftKey ? "t2" : "t1";
    const direction = e.key === "ArrowLeft" ? -1 : 1;
    const samples = this.findPrimaryChannel()?.buffer.snapshot();
    let step: number;
    if (samples && samples.times.length > 1) {
      const first = samples.times[0] ?? 0;
      const last = samples.times[samples.times.length - 1] ?? first;
      const span = last - first;
      step = span / Math.max(1, samples.times.length - 1);
    } else {
      step = (this.viewport.timeBase.value * 10) / 100; // 1% of window
    }
    e.preventDefault();
    nudgeCursor(which, direction * step);
  };

  // ── topbar ──────────────────────────────────────────────────────

  private renderTopBar(): TemplateResult {
    const live = this._live.value;
    const running = this._running.value;
    const result = this._result.value;
    const tb = this.viewport.timeBase.value;
    const cursors = this._cursors.value;
    const dt = this._cursorDt.value;
    const invDt = this._cursorInvDt.value;
    // Sprint 80.Z A2 — reactive chevron + aria for the collapse
    // button (Şefim #3 closure).
    const expanded = this._expanded.value;
    const readout = cursors.enabled
      ? html`<span class="cursor-readout">
          T1=<em>${formatSeconds(cursors.t1)}</em> · T2=<em>${formatSeconds(cursors.t2)}</em> ·
          Δt=<em>${formatSeconds(dt)}</em> · 1/Δt=<em
            >${invDt !== null ? formatHertz(invDt) : "—"}</em
          >
        </span>`
      : null;
    return html`<div class="topbar">
      <button data-action="run-once" title="F5">▶ Run</button>
      <button data-action="toggle-live" title="Shift+F5">${live ? "■ Stop" : "● Live"}</button>
      <span class="${live ? "live" : "ready"}">
        ${live
          ? "● LIVE"
          : running
            ? "… RUN"
            : result
              ? `✓ ${String(result.samples)} samples`
              : "READY"}
      </span>
      <span class="timebase">${formatTimeBase(tb)}/div</span>
      ${readout}
      <span style="margin-left:auto">
        <button
          data-action="toggle-cursors"
          aria-pressed=${cursors.enabled ? "true" : "false"}
          title="Δt imleçler"
        >
          ${cursors.enabled ? "◉" : "◎"} Δt
        </button>
        <button
          data-action="collapse"
          aria-pressed=${expanded ? "false" : "true"}
          aria-label=${expanded ? "Osiloskop panelini sıkıştır" : "Osiloskop panelini genişlet"}
          title=${expanded ? "Sıkıştır (▾)" : "Genişlet (▴)"}
        >
          ${expanded ? "▾" : "▴"}
        </button>
      </span>
    </div>`;
  }

  private renderCursorOverlay(): TemplateResult | typeof nothing {
    const cursors = this._cursors.value;
    if (!cursors.enabled) return nothing;
    const t1Pct = this.timeToPercent(cursors.t1);
    const t2Pct = this.timeToPercent(cursors.t2);
    const labelPct = (t1Pct + t2Pct) / 2;
    const dt = this._cursorDt.value;
    return html`<div class="cursor-overlay" data-testid="cursor-overlay">
      <div class="cursor-line" style="left:${t1Pct}%" data-cursor-line="t1"></div>
      <div class="cursor-line" style="left:${t2Pct}%" data-cursor-line="t2"></div>
      <div
        class="cursor-handle"
        data-cursor-handle="t1"
        style="left:${t1Pct}%"
        @pointerdown=${this.handleCursorPointerDown}
        @pointermove=${this.handleCursorPointerMove}
        @pointerup=${this.handleCursorPointerUp}
        @pointercancel=${this.handleCursorPointerUp}
        role="slider"
        aria-label="T1"
        aria-valuetext=${formatSeconds(cursors.t1)}
      ></div>
      <div
        class="cursor-handle"
        data-cursor-handle="t2"
        style="left:${t2Pct}%"
        @pointerdown=${this.handleCursorPointerDown}
        @pointermove=${this.handleCursorPointerMove}
        @pointerup=${this.handleCursorPointerUp}
        @pointercancel=${this.handleCursorPointerUp}
        role="slider"
        aria-label="T2"
        aria-valuetext=${formatSeconds(cursors.t2)}
      ></div>
      <div class="cursor-label" style="left:${labelPct}%" data-testid="cursor-label">
        Δt = ${formatSeconds(dt)}
      </div>
    </div>`;
  }

  private renderMeasurementPanel(): TemplateResult[] {
    const tiles: TemplateResult[] = [];
    const primary = this.findPrimaryChannel();
    const samples = primary?.buffer.snapshot();
    // Six standard tiles run through the MEASUREMENTS map.
    for (const tile of this.measurementTiles()) {
      tiles.push(this.renderMeasurementTile(tile, samples));
    }
    // Sprint 18 Paket C C2 — two cursor-derived virtual tiles.
    tiles.push(this.renderCursorTile("cursor-dt"));
    tiles.push(this.renderCursorTile("cursor-invdt"));
    return [html`<div class="measurements">${tiles}</div>`];
  }

  private measurementTiles(): readonly MeasurementTile[] {
    // Order mirrors zenit-platform.html line 2312-2335.
    return [
      { id: "vrms", labelKey: "waveform.measure.vrms" },
      { id: "vmax", labelKey: "waveform.measure.vpeak" },
      { id: "vpp", labelKey: "waveform.measure.vpp" },
      { id: "frequency", labelKey: "waveform.measure.frequency" },
      { id: "period", labelKey: "waveform.measure.period" },
      { id: "duty-cycle", labelKey: "waveform.measure.duty" },
    ];
  }

  private renderMeasurementTile(
    tile: MeasurementTile,
    samples: { times: Float64Array; values: Float64Array } | undefined,
  ): TemplateResult {
    if (tile.id === "cursor-dt" || tile.id === "cursor-invdt") {
      return this.renderCursorTile(tile.id);
    }
    const fn = MEASUREMENTS[tile.id];
    const result = samples ? fn(samples.values, this.sampleRate) : null;
    const valueText =
      result?.value !== null && result?.value !== undefined
        ? result.value.toPrecision(result.precision)
        : "—";
    const unit = result?.unit ?? "";
    return html`<div class="measurement-tile" data-tile=${tile.id}>
      <span class="label">${t(tile.labelKey)}</span>
      <span class="value">${valueText}</span>
      <span class="unit">${unit}</span>
    </div>`;
  }

  /** Sprint 18 Paket C C2 — virtual tile that reads the cursor
   *  store rather than the MEASUREMENTS map. Disabled cursors
   *  paint a dash so the layout is stable. */
  private renderCursorTile(id: CursorTileId): TemplateResult {
    const cursors = this._cursors.value;
    const dt = this._cursorDt.value;
    const invDt = this._cursorInvDt.value;
    const labelKey: TranslationKey =
      id === "cursor-dt" ? "waveform.measure.deltaT" : "waveform.measure.invDeltaT";
    let valueText = "—";
    let unit = "";
    if (cursors.enabled) {
      if (id === "cursor-dt") {
        const formatted = formatSeconds(dt);
        const parts = splitValueUnit(formatted);
        valueText = parts.value;
        unit = parts.unit;
      } else if (invDt !== null) {
        const formatted = formatHertz(invDt);
        const parts = splitValueUnit(formatted);
        valueText = parts.value;
        unit = parts.unit;
      }
    }
    return html`<div class="measurement-tile" data-tile=${id}>
      <span class="label">${t(labelKey)}</span>
      <span class="value">${valueText}</span>
      <span class="unit">${unit}</span>
    </div>`;
  }

  private renderChannelControls(): TemplateResult[] {
    const items: TemplateResult[] = [];
    void this.channelViewSignal.value;
    for (const id of CHANNEL_IDS) {
      const state = channels.value.get(id);
      const enabled = state?.enabled ?? false;
      items.push(
        html`<div
          class="channel-chip"
          data-enabled=${String(enabled)}
          data-action="toggle-channel"
          data-channel=${id}
          title="Toggle ${id.toUpperCase()}"
        >
          <span class="swatch" style="background:${swatchColor(id)}"></span>
          <span>${id.toUpperCase()}</span>
        </div>`,
      );
    }
    // Math expression inputs
    for (const id of MATH_CHANNEL_IDS) {
      const state = channels.value.get(id);
      const expr = state?.expression ?? "";
      const parsed = expr ? parseMathExpression(expr) : null;
      items.push(
        html`<input
          class="math-input"
          data-input="math-expr"
          data-channel=${id}
          placeholder="${id.toUpperCase()} expression (e.g. ch1 + ch2)"
          value=${expr}
        />`,
      );
      if (parsed?.error) {
        items.push(html`<span class="math-error">${parsed.error}</span>`);
      }
    }
    return [html`<div class="channels">${items}</div>`];
  }

  // ── event delegation ────────────────────────────────────────────

  private handleAction(e: Event): void {
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;
    const trigger = target.closest<HTMLElement>("[data-action]");
    if (!trigger) return;
    const action = trigger.dataset.action ?? "";
    switch (action) {
      case "run-once": {
        const c = currentCircuit.value;
        if (c) void runTransient(c);
        break;
      }
      case "toggle-live": {
        if (transientLive.value) {
          stopLive();
        } else {
          startLive(() => currentCircuit.value);
        }
        break;
      }
      case "collapse":
        // Sprint 80.Z A2 (Şefim #3 closure) — toggle, not one-way.
        // Pre-fix the handler hard-set waveformExpanded.value = false
        // every click, leaving no in-pane way back to the expanded
        // 260 px state once collapsed to the 24 px strip. The user's
        // tour observation "gizleme butonu tersine çalışıyor"
        // mapped to this irreversibility (the collapse button is
        // at the right edge of the waveform topbar — Şefim called
        // the surface "sidebar"). L-A-2 Sandık Kapağı topbar chip
        // provides the canonical full-hide reversible toggle; this
        // fix restores the in-pane expand/collapse parity.
        waveformExpanded.value = !waveformExpanded.value;
        break;
      case "toggle-cursors":
        toggleCursors();
        break;
      case "toggle-channel": {
        const id = trigger.dataset.channel as ChannelId | undefined;
        if (!id) return;
        const state = channels.value.get(id);
        if (state?.probeBinding) {
          unbindProbe(id);
        } else {
          toggleChannel(id);
        }
        break;
      }
      default:
        return;
    }
  }

  private handleInput(e: Event): void {
    const target = e.target as HTMLElement | null;
    if (!target?.dataset.input) return;
    if (target.dataset.input === "math-expr") {
      const id = target.dataset.channel as "m1" | "m2" | undefined;
      if (!id) return;
      const value = (target as HTMLInputElement).value;
      const m1 =
        id === "m1"
          ? parseMathExpression(value)
          : parseMathExpression(channels.value.get("m1")?.expression ?? "");
      const m2 =
        id === "m2"
          ? parseMathExpression(value)
          : parseMathExpression(channels.value.get("m2")?.expression ?? "");
      if (detectCycle(m1, m2)) {
        // Reject — keep the previous expression. UI surfaces the
        // error via the parsed.error overlay because the input
        // keeps the user's text but the store is unchanged.
        return;
      }
      setMathExpression(id, value);
      this.recomputeMathChannels();
    }
  }

  // ── housekeeping ────────────────────────────────────────────────

  /** Re-derive the WaveformRenderer's channel view signal from the
   *  store + projector for the math channels. */
  private refreshChannelViewSignal(): void {
    const next = new Map<ChannelId, ChannelView>();
    for (const id of CHANNEL_IDS) {
      const state = channels.value.get(id);
      if (!state) continue;
      next.set(id, {
        id,
        enabled: state.enabled,
        buffer: state.buffer,
      });
    }
    this.channelViewSignal.value = next;
  }

  private refreshSampleRate(): void {
    const result = currentTransientResult.value;
    if (!result || result.timeSeries.length < 2) return;
    const first = result.timeSeries[0];
    const last = result.timeSeries[result.timeSeries.length - 1];
    if (!first || !last) return;
    const span = last.t - first.t;
    if (span <= 0) return;
    this.sampleRate = (result.timeSeries.length - 1) / span;
  }

  private findPrimaryChannel(): ChannelView | null {
    for (const id of CHANNEL_IDS) {
      const state = channels.value.get(id);
      if (state?.enabled && state.buffer.size > 0) {
        return { id, enabled: true, buffer: state.buffer };
      }
    }
    return null;
  }

  /** Recompute math channel buffers from the upstream probe data.
   *  Math channels do not own a transient solve — they are pure
   *  AST walks over the existing CH buffers. */
  private recomputeMathChannels(): void {
    const probeSamples: Partial<Record<ChannelId, Float64Array>> = {};
    for (const id of PROBE_CHANNEL_IDS) {
      const buf = channels.value.get(id)?.buffer.snapshot();
      if (buf) probeSamples[id] = buf.values;
    }
    for (const id of MATH_CHANNEL_IDS) {
      const state = channels.value.get(id);
      if (!state?.expression) continue;
      const expr = parseMathExpression(state.expression);
      if (expr.error || !expr.ast) continue;
      const result = evaluateMath(expr, probeSamples);
      if (!result) continue;
      // Replace the math buffer's contents with the recomputed samples.
      state.buffer.clear();
      const times = channels.value.get("ch1")?.buffer.snapshot().times;
      for (let i = 0; i < result.length; i++) {
        state.buffer.push(times?.[i] ?? i, result[i] ?? 0);
      }
    }
  }

  private readonly applyResize = (): void => {
    if (!this.canvasEl) return;
    const dpr = window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
    const rect = this.canvasEl.getBoundingClientRect();
    this.canvasEl.width = Math.max(1, Math.floor(rect.width * dpr));
    this.canvasEl.height = Math.max(1, Math.floor(rect.height * dpr));
    this.renderer?.setDPR(dpr);
    this.renderer?.invalidate();
  };
}

function swatchColor(id: ChannelId): string {
  switch (id) {
    case "ch1":
      return ZENIT_THEME.channel.ch1;
    case "ch2":
      return ZENIT_THEME.channel.ch2;
    case "ch3":
      return ZENIT_THEME.channel.ch3;
    case "ch4":
      return ZENIT_THEME.channel.ch4;
    case "m1":
      return ZENIT_THEME.accent.brass;
    case "m2":
      return ZENIT_THEME.accent.brassHot;
  }
}

function formatTimeBase(seconds: number): string {
  if (seconds >= 1) return `${seconds.toFixed(0)} s`;
  if (seconds >= 1e-3) return `${(seconds * 1e3).toFixed(0)} ms`;
  if (seconds >= 1e-6) return `${(seconds * 1e6).toFixed(0)} µs`;
  return `${(seconds * 1e9).toFixed(0)} ns`;
}

/**
 * Format a time-axis value with engineering units. The cursor
 * readout consumes this both for absolute positions (T1, T2) and
 * the delta (Δt). Three significant digits keep the topbar tight
 * while still matching ngspice scope-print precision.
 */
export function formatSeconds(seconds: number): string {
  if (!Number.isFinite(seconds)) return "—";
  const abs = Math.abs(seconds);
  if (abs === 0) return "0 s";
  if (abs >= 1) return `${seconds.toFixed(3)} s`;
  if (abs >= 1e-3) return `${(seconds * 1e3).toFixed(3)} ms`;
  if (abs >= 1e-6) return `${(seconds * 1e6).toFixed(3)} µs`;
  return `${(seconds * 1e9).toFixed(1)} ns`;
}

/** Mirror helper for `1/Δt`. Hz / kHz / MHz to keep the readout
 *  legible across the full range our adaptive transient produces. */
export function formatHertz(hz: number): string {
  if (!Number.isFinite(hz) || hz <= 0) return "—";
  if (hz >= 1e6) return `${(hz / 1e6).toFixed(3)} MHz`;
  if (hz >= 1e3) return `${(hz / 1e3).toFixed(3)} kHz`;
  return `${hz.toFixed(1)} Hz`;
}

/**
 * Sprint 18 Paket C C2 — the measurement-tile layout puts the
 * numeric value and the unit on separate spans (value gets the
 * larger font, unit dims to brass-faint italic). `formatSeconds`
 * + `formatHertz` return a single string; this splits that into
 * the two halves on the last whitespace.
 */
export function splitValueUnit(formatted: string): { value: string; unit: string } {
  const trimmed = formatted.trim();
  if (trimmed === "—" || trimmed === "") return { value: "—", unit: "" };
  const lastSpace = trimmed.lastIndexOf(" ");
  if (lastSpace < 0) return { value: trimmed, unit: "" };
  return { value: trimmed.slice(0, lastSpace), unit: trimmed.slice(lastSpace + 1) };
}

void setVDiv;

declare global {
  interface HTMLElementTagNameMap {
    "vxa-waveform": VxaWaveform;
  }
}
